<template>
  <div class="ai-follow-up-assistant-config">
    <div class="ai-follow-up-assistant-config__card">
      <div class="ai-follow-up-assistant-config__header">
        <div class="ai-follow-up-assistant-config__title">
          {{ currentPromptConfig.title }}
        </div>
        <div class="ai-follow-up-assistant-config__desc">
          {{ currentPromptConfig.desc }}
        </div>
      </div>
      <div class="ai-follow-up-assistant-config__body">
        <div v-show="!polishLoading" class="ai-follow-up-assistant-config__textarea-wrap">
          <el-input
            v-model="currentRuleContent"
            class="ai-follow-up-assistant-config__textarea"
            :maxlength="2000"
            :placeholder="currentPromptConfig.placeholder"
            show-word-limit
            type="textarea"
            @input="checkChangeAiPrompt"
          />
        </div>
        <div v-show="polishLoading" class="ai-follow-up-assistant-config__skeleton">
          <div class="ai-follow-up-assistant-config__skeleton-inner">
            <el-skeleton animated>
              <template #template>
                <div class="ai-follow-up-assistant-config__skeleton-rows">
                  <el-skeleton-item
                    class="ai-follow-up-assistant-config__skeleton-row"
                    variant="text"
                  />
                  <el-skeleton-item
                    class="ai-follow-up-assistant-config__skeleton-row"
                    variant="text"
                  />
                  <el-skeleton-item
                    class="ai-follow-up-assistant-config__skeleton-row ai-follow-up-assistant-config__skeleton-row--short"
                    variant="text"
                  />
                  <el-skeleton-item
                    class="ai-follow-up-assistant-config__skeleton-row ai-follow-up-assistant-config__skeleton-row--medium"
                    variant="text"
                  />
                </div>
              </template>
            </el-skeleton>
          </div>
        </div>
        <div class="ai-follow-up-assistant-config__polish">
          <AiPolish
            ref="aiPolishRef"
            :disabled="!currentRuleContent.trim() || polishLoading"
            :params="polishParams"
            @end="handlePolishEnd"
            @start="handlePolishStart"
            @success="handlePolishSuccess"
            @withdraw="handleWithdrawPolish"
          />
        </div>
        <el-button
          class="ai-follow-up-assistant-config__save"
          color="#7a52f4"
          :loading="saveLoading"
          type="primary"
          @click="handleSave"
        >
          {{ saveButtonText }}
        </el-button>
      </div>
    </div>
    <div class="ai-follow-up-assistant-config__pager">
      <i
        v-for="(item, index) in promptDetails"
        :key="item.promptAlias || index"
        class="t-iconfont"
        :class="getPagerIconClass(item)"
      ></i>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useAppStore } from '@/store/useAppStore'
import {
  editInitialization,
  checkInitializationComplete,
  updateQualityAnalysisRule
} from '@/api/index'
import { ruleSave } from '@/api/ai-board'
import { ElMessage } from 'element-plus'
import AppMessage from '@/components/message/index.js'
import emitter from '@/utils/mitt'
import utils from '@/utils'
import AiPolish from '@/components/ai-polish/index.vue'

defineOptions({
  name: 'AiFollowUpAssistantConfig'
})

const props = defineProps({
  isOutLast: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['saveSuccess'])

const appStore = useAppStore()
const activePromptAlias = ref('')
const saveLoading = ref(false)
const aiPolishRef = ref(null)
const polishLoading = ref(false)
const lastOriginalRuleContent = ref('')
const qualityAnalysisRuleId = ref('')
const qualityAnalysisRuleConfig = ref([])
const ruleContentMap = ref({
  audioVisual: '',
  callContent: '',
  chatHistory: '',
  qualityAnalysis: ''
})

const promptConfigMap = {
  audioVisual: {
    title: '对于线下沟通、线上会议形式的跟进，您希望提炼出哪些内容',
    desc: '请按你们平时最关心的内容来填写，可以让 AI 帮你润色成更清晰的表达',
    placeholder: '示例：\n更希望提炼客户真实需求、异议点、销售是否推进到了下一步，以及后续待办。'
  },
  callContent: {
    title: '对于电话形式的跟进，您希望可以提炼出哪些内容',
    desc: '请按你们平时最关心的内容来填写，可以让 AI 帮你润色成更清晰的表达',
    placeholder:
      '示例：\n希望看电话是否触达核心需求、客户是否愿意继续推进，以及电话里暴露出的风险点。'
  },
  chatHistory: {
    title: '对于微信/企微等聊天形式的跟进，您希望可以提炼出哪些内容',
    desc: '请按你们平时最关心的内容来填写，可以让 AI 帮你润色成更清晰的表达',
    placeholder: '示例：\n希望看聊天里客户最关心什么、有没有阻塞点、下一步有没有约清楚。'
  },
  qualityAnalysis: {
    title: '您认为什么样的跟进是有风险需要关注的',
    desc: '请按你们平时最关心的内容来填写，可以让 AI 帮你润色成更清晰的表达',
    placeholder:
      '示例：\n需要重点关注客户明确表达异议但销售没有有效回应、沟通没有推进下一步、客户意向明显下降等情况。'
  }
}

const defaultPromptConfig = promptConfigMap.audioVisual

const promptDetails = computed(() => {
  const config = appStore.aiInitConfigDetails.find(
    (item) => item.configAlias === 'followUpAssistantConfig'
  )

  return config?.configDetails || []
})

const isQualityAnalysisPrompt = computed(() => activePromptAlias.value === 'qualityAnalysis')

const currentPromptConfig = computed(() => {
  return promptConfigMap[activePromptAlias.value] || defaultPromptConfig
})

const currentRuleContent = computed({
  get() {
    return ruleContentMap.value[activePromptAlias.value] || ''
  },
  set(value) {
    ruleContentMap.value[activePromptAlias.value] = value
  }
})

const polishParams = computed(() => ({
  sceneType: 2,
  userPrompt: currentRuleContent.value
}))

const isLastIncompletePrompt = computed(() => {
  const lastPrompt = promptDetails.value[promptDetails.value.length - 1]
  return lastPrompt?.promptAlias === activePromptAlias.value
})

const saveButtonText = computed(() => {
  if (isLastIncompletePrompt.value) {
    if (props.isOutLast) {
      return '完成'
    } else {
      return '已完成此步骤配置'
    }
  } else {
    return '确定并保存'
  }
})

const findNextPrompt = (details, currentIndex) => {
  return details[currentIndex + 1]
}

const findDefaultPromptAlias = (details) => {
  return details.find((item) => item.isComplete !== 1)?.promptAlias || details[0]?.promptAlias || ''
}

const getPagerIconClass = (item) => {
  if (item.isComplete === 1) {
    return ['icon-checkbox-circle-fill', 'ai-follow-up-assistant-config__pager-done']
  }

  if (item.promptAlias === activePromptAlias.value) {
    return ['icon-radio-button-line', 'ai-follow-up-assistant-config__pager-active']
  }

  return ['icon-checkbox-blank-circle-line']
}

// const handleClickPager = (item) => {
//   if (item.isComplete === 1) return
//   resetPolish()
//   activePromptAlias.value = item.promptAlias
// }

const resetPolish = () => {
  aiPolishRef.value?.reset()
  polishLoading.value = false
  lastOriginalRuleContent.value = ''
}

const handlePolishSuccess = (result) => {
  if (!result) return
  lastOriginalRuleContent.value = currentRuleContent.value
  currentRuleContent.value = result
}

const handlePolishStart = () => {
  polishLoading.value = true
}

const handlePolishEnd = () => {
  polishLoading.value = false
}

const handleWithdrawPolish = () => {
  if (!lastOriginalRuleContent.value) return
  currentRuleContent.value = lastOriginalRuleContent.value
  lastOriginalRuleContent.value = ''
}

const checkChangeAiPrompt = (value) => {
  if (aiPolishRef.value?.success && lastOriginalRuleContent.value !== value) {
    aiPolishRef.value.reset()
  }
}

const getUpdatedConfigDetails = (userId, operateTime) => {
  let currentPromptIndex = -1
  const configDetails = appStore.aiInitConfigDetails.map((item) => {
    if (item.configAlias !== 'followUpAssistantConfig') return { ...item }

    return {
      ...item,
      configDetails: (item.configDetails || []).map((detail, index) => {
        if (detail.promptAlias !== activePromptAlias.value) return { ...detail }
        currentPromptIndex = index
        return {
          ...detail,
          isComplete: 1,
          operator: userId,
          operateTime
        }
      })
    }
  })
  const followUpConfig = configDetails.find(
    (item) => item.configAlias === 'followUpAssistantConfig'
  )

  return {
    configDetails,
    nextPrompt: findNextPrompt(followUpConfig?.configDetails || [], currentPromptIndex)
  }
}

const getRuleSaveParams = () => {
  const baseParams = {
    priority: 0, // 默认不传，初始化传0
    isLiberalRules: 1,
    liberalRule: currentRuleContent.value,
    isDefault: 0,
    name: '规则名称',
    userRangePojo: []
  }

  if (activePromptAlias.value === 'audioVisual') {
    return {
      ...baseParams,
      type: 0,
      metricRulePojo: {
        isOpenBehaviorMetricRule: 1,
        contentMetricRule: {
          summary: [
            {
              itemName: '',
              itemDesc: '',
              itemValues: [
                {
                  ruleName: '',
                  ruleValue: ''
                }
              ]
            }
          ]
        },
        todoMetricRule: []
      }
    }
  }

  return {
    ...baseParams,
    type: activePromptAlias.value === 'callContent' ? 1 : 2
  }
}

const updateTheQualityAnalysisRule = () => {
  return updateQualityAnalysisRule({
    id: qualityAnalysisRuleId.value,
    isLiberalRules: 1,
    liberalRule: currentRuleContent.value,
    rule: { summary: qualityAnalysisRuleConfig.value }
  })
}

const syncRuleContentMapFromStore = (details) => {
  if (!details?.length) return

  const nextRuleContentMap = { ...ruleContentMap.value }

  details.forEach((item) => {
    if (!(item.promptAlias in nextRuleContentMap)) return
    nextRuleContentMap[item.promptAlias] = item.aiGeneratedContent || ''
  })

  ruleContentMap.value = nextRuleContentMap
}

const validateRuleContent = () => {
  if (isQualityAnalysisPrompt.value) {
    if (currentRuleContent.value.trim()) return true

    ElMessage.error('请输入自由规则内容')
    return false
  }

  const contentLength = currentRuleContent.value.length
  if (contentLength >= 10 && contentLength <= 2000) return true

  ElMessage.error('自由规则字数应在10-2000之间')
  return false
}

const handleSave = async () => {
  if (saveLoading.value) return
  if (!validateRuleContent()) return

  if (!isQualityAnalysisPrompt.value) {
    const checkRes = await checkInitializationComplete({
      configAlias: 'followUpAssistantConfig',
      promptAlias: activePromptAlias.value
    })
    if (checkRes?.result?.isCompleted === 1) {
      AppMessage.info(`该步配置已被「${checkRes.result.operatorName}」完成`)
      emitter.emit('getInitializationConfig')
      saveLoading.value = false
      return
    }
  }
  saveLoading.value = true

  const _isLastIncompletePrompt = isLastIncompletePrompt.value
  try {
    const operateTime = Math.floor(Date.now() / 1000)
    const userId = utils.xbbLocalStorageGet('userId') || ''
    const { configDetails, nextPrompt } = getUpdatedConfigDetails(userId, operateTime)
    if (isQualityAnalysisPrompt.value) {
      await updateTheQualityAnalysisRule()
    } else {
      await ruleSave(getRuleSaveParams())
    }
    await editInitialization({ configDetails })
    appStore.setAiInitConfigDetails(configDetails)

    if (nextPrompt) {
      resetPolish()
      activePromptAlias.value = nextPrompt.promptAlias
    }
    if (_isLastIncompletePrompt) {
      emit('saveSuccess')
    }
  } finally {
    saveLoading.value = false
  }
}

watch(
  promptDetails,
  (details) => {
    syncRuleContentMapFromStore(details)
    const activePrompt = details.find((item) => item.promptAlias === activePromptAlias.value)
    const hasIncompletePrompt = details.some((item) => item.isComplete !== 1)

    if (!activePrompt || (activePrompt.isComplete === 1 && hasIncompletePrompt)) {
      resetPolish()
      activePromptAlias.value = findDefaultPromptAlias(details)
    }
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.ai-follow-up-assistant-config {
  width: 100%;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.ai-follow-up-assistant-config__card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  overflow: hidden;
}

.ai-follow-up-assistant-config__header {
  flex: 0 0 auto;
  padding: 16px 16px 0;
  box-sizing: border-box;
}

.ai-follow-up-assistant-config__header::after {
  content: '';
  display: block;
  height: 1px;
  margin-top: 12px;
  background: #e5e6eb;
}

.ai-follow-up-assistant-config__title {
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: #323233;
}

.ai-follow-up-assistant-config__desc {
  margin-top: 4px;
  font-size: 14px;
  line-height: 20px;
  color: #969799;
}

.ai-follow-up-assistant-config__body {
  flex: 1;
  min-height: 0;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.ai-follow-up-assistant-config__textarea-wrap {
  flex: 1;
  min-height: 0;
  padding: 0;
  box-sizing: border-box;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.ai-follow-up-assistant-config__textarea {
  height: 100%;
}

.ai-follow-up-assistant-config__textarea :deep(.el-textarea__inner) {
  width: 100%;
  height: 100%;
  min-height: 100% !important;
  padding: 8px 12px;
  box-sizing: border-box;
  border: none;
  box-shadow: none;
  outline: none;
  resize: none;
  font-family: 'PingFang SC', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #323233;
}

.ai-follow-up-assistant-config__textarea :deep(.el-textarea__inner::placeholder) {
  color: #c8c9cc;
}

.ai-follow-up-assistant-config__textarea :deep(.el-input__count) {
  right: 12px;
  bottom: 6px;
  color: #969799;
}

.ai-follow-up-assistant-config__polish {
  width: fit-content;
  height: 16px;
  margin-top: 16px;
  padding: 0;
  display: flex;
  align-items: center;
}

.ai-follow-up-assistant-config__skeleton {
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.ai-follow-up-assistant-config__skeleton-inner {
  padding: 12px;
}

.ai-follow-up-assistant-config__skeleton-row {
  width: 100%;
  height: 18px;
  margin-bottom: 12px;
}

.ai-follow-up-assistant-config__skeleton-row--short {
  width: 64%;
}

.ai-follow-up-assistant-config__skeleton-row--medium {
  width: 82%;
}

.ai-follow-up-assistant-config__skeleton-row:last-child {
  margin-bottom: 0;
}

.ai-follow-up-assistant-config__save {
  width: max-content;
  height: 32px;
  margin-top: 16px;
  border-radius: 8px;
}

.ai-follow-up-assistant-config__pager {
  flex: 0 0 auto;
  height: 20px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-follow-up-assistant-config__pager i {
  margin-right: 16px;
  font-size: 20px;
  line-height: 20px;
  color: #c8c9cc;
}

.ai-follow-up-assistant-config__pager i:last-child {
  margin-right: 0;
}

.ai-follow-up-assistant-config__pager .ai-follow-up-assistant-config__pager-active {
  color: #7a52f4;
}

.ai-follow-up-assistant-config__pager .ai-follow-up-assistant-config__pager-done {
  color: #00c241;
  cursor: not-allowed;
}
</style>
